import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { FlexxiQuote } from "@gaole/shared";
import { FLEXXI_KYC_GATE_CODE } from "@gaole/shared";
import { api, ApiError } from "../../lib/api";
import { rm } from "../../lib/format";
import { openPaymentGateway } from "../../lib/payments";
import { paymentMethodLabel, primaryPayButtonLabel, sortPaymentMethods } from "../../lib/paymentMethods";
import { useAuth } from "../../stores/auth";
import { toast } from "../../stores/toast";
import { Button } from "../ui";
import { PolicyConsentCheckbox } from "../PolicyConsentCheckbox";
import { FlexxiPlanCollapsible } from "../flexxi/FlexxiPlanCollapsible";
import { FlexxiBreakdownTable } from "../flexxi/FlexxiBreakdownTable";
import { flexxiQuoteHint } from "../shop/FlexxiShopPanel";

const fieldClass =
  "mt-1 w-full rounded-xl border border-white/10 bg-surface-2 px-3 py-2 text-sm text-white outline-none focus:border-white/20 focus:ring-0";

export function FlexxiMarketplacePanel({
  listingId,
  quantity,
}: {
  listingId: string;
  quantity: number;
  listingTitle?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, config, moduleEnabled } = useAuth();
  const [templateId, setTemplateId] = useState("");
  const [method, setMethod] = useState("");
  const [acceptPolicies, setAcceptPolicies] = useState(false);
  const [busy, setBusy] = useState(false);

  const enabled =
    Boolean(user && config?.flexxiEnabled && moduleEnabled("instalments") && moduleEnabled("vault"));

  const templatesQ = useQuery({
    queryKey: ["flexxi", "templates"],
    queryFn: () =>
      api<{
        templates: { id: string; code: string; name: string; termMonths: number }[];
        slots: { active: number; limit: number; reputationScore: number };
        kyc: { kycVerified: boolean };
      }>("/flexxi/templates"),
    enabled,
  });

  const quoteQ = useQuery({
    queryKey: ["flexxi", "quote", "market", listingId, quantity, templateId],
    queryFn: () =>
      api<{ quote: FlexxiQuote }>("/flexxi/quote", {
        method: "POST",
        body: { templateId, listingId, channel: "MARKETPLACE", quantity },
      }),
    enabled: enabled && Boolean(templateId) && (templatesQ.data?.kyc?.kycVerified ?? false),
  });

  const methods = useMemo(() => {
    const pm = config?.paymentMethods;
    const list: string[] = [];
    if (pm?.bankIn) list.push("BANK_IN");
    if (config?.creditsEnabled) list.push("CREDITS");
    if (pm?.gkash) list.push("GKASH");
    if (pm?.stripe) list.push("STRIPE");
    return sortPaymentMethods(list);
  }, [config]);

  const quote = quoteQ.data?.quote;
  const dueLabel = quote ? rm(quote.dueToday) : "—";
  const templates = templatesQ.data?.templates ?? [];
  const kycVerified = templatesQ.data?.kyc?.kycVerified ?? false;

  useEffect(() => {
    if (!templateId && templates[0]) setTemplateId(templates[0].id);
  }, [templateId, templates]);

  if (!enabled || templatesQ.isError || (!templatesQ.isLoading && !templates.length)) return null;

  if (!kycVerified) {
    return (
      <FlexxiPlanCollapsible>
        <p className="text-[11px] leading-relaxed text-white/45">
          Verify your identity to use FlexxiPlan on marketplace listings.
        </p>
        <Button variant="outline" className="w-full !min-h-9 text-xs" onClick={() => navigate("/account/flexxi/verify")}>
          Verify for FlexxiPlan
        </Button>
      </FlexxiPlanCollapsible>
    );
  }

  async function startFlexxiCheckout() {
    if (!user) {
      navigate("/login");
      return;
    }
    if (!templateId || !method) {
      toast("error", "Choose a plan and payment method");
      return;
    }
    if (!acceptPolicies) {
      toast("error", "Accept the store policies to continue");
      return;
    }
    if (!quote) {
      toast("error", "Couldn't load plan quote — try again");
      return;
    }
    setBusy(true);
    try {
      const res = await api<{
        orderId: string;
        quote: FlexxiQuote;
        payment: {
          paid?: boolean;
          status?: string;
          redirectUrl?: string;
          form?: { url: string; fields: Record<string, string> };
        };
      }>("/flexxi/checkout/deposit", {
        method: "POST",
        body: {
          templateId,
          listingId,
          channel: "MARKETPLACE",
          quantity,
          method,
          acceptPolicies: true,
          idempotencyKey: crypto.randomUUID(),
          fulfilmentMethod: "DELIVERY",
        },
      });

      const payment = res.payment;
      if (payment.paid) {
        toast("success", "FlexxiPlan started");
        await queryClient.invalidateQueries({ queryKey: ["flexxi"] });
        navigate("/account/flexxi");
        return;
      }
      if (payment.status === "REDIRECT" && payment.redirectUrl) {
        openPaymentGateway({ status: "REDIRECT", redirectUrl: payment.redirectUrl });
        return;
      }
      if (payment.status === "FORM" && payment.form) {
        openPaymentGateway({ status: "GKASH_FORM", form: payment.form });
        return;
      }
      navigate(`/checkout/order/${res.orderId}`);
    } catch (err) {
      toast("error", err instanceof ApiError ? err.message : "Flexxi checkout failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <FlexxiPlanCollapsible hint={flexxiQuoteHint(quote)}>
      <p className="text-[11px] leading-relaxed text-white/45">
        Pay today; card held in your vault until instalments finish, then the seller fulfils your order.
        {templatesQ.data?.slots ? (
          <span className="block pt-1 text-white/35">
            Active plans {templatesQ.data.slots.active}/{templatesQ.data.slots.limit}
          </span>
        ) : null}
      </p>

      {templatesQ.isLoading ? (
        <p className="text-[11px] text-white/40">Loading plans…</p>
      ) : (
        <>
          <label className="block text-[10px] uppercase tracking-wide text-white/40">
            Plan
            <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={fieldClass}>
              <option value="">Select term…</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.termMonths} mo)
                </option>
              ))}
            </select>
          </label>

          {quoteQ.isFetching && templateId ? <p className="text-[11px] text-white/40">Calculating…</p> : null}

          {quote && !quoteQ.isError ? (
            <FlexxiBreakdownTable quote={quote} />
          ) : quoteQ.isError ? (
            <p className="text-[11px] text-amber-400/90">
              {quoteQ.error instanceof ApiError && quoteQ.error.code === FLEXXI_KYC_GATE_CODE ? (
                <>
                  {quoteQ.error.message}{" "}
                  <button type="button" className="underline" onClick={() => navigate("/account/flexxi/verify")}>
                    Verify now
                  </button>
                </>
              ) : quoteQ.error instanceof ApiError ? (
                quoteQ.error.message
              ) : (
                "Not eligible for FlexxiPlan"
              )}
            </p>
          ) : null}

          {quote && methods.length > 0 ? (
            <>
              <label className="block text-[10px] uppercase tracking-wide text-white/40">
                Pay today with
                <select value={method} onChange={(e) => setMethod(e.target.value)} className={fieldClass}>
                  <option value="">Select…</option>
                  {methods.map((m) => (
                    <option key={m} value={m}>
                      {paymentMethodLabel(m)}
                    </option>
                  ))}
                </select>
              </label>
              <PolicyConsentCheckbox variant="checkout" checked={acceptPolicies} onChange={setAcceptPolicies} />
              <Button
                variant="outline"
                className="w-full"
                disabled={busy || !method || quoteQ.isFetching}
                onClick={() => void startFlexxiCheckout()}
              >
                {busy ? "Starting…" : method ? primaryPayButtonLabel(method, dueLabel) : "Choose payment method"}
              </Button>
            </>
          ) : null}
        </>
      )}
    </FlexxiPlanCollapsible>
  );
}
